import useSWR from 'swr'
import { captureExceptionWithSentry } from '../sentry'
import apiFetch from '../common/apiFetch'
import { AdFormats } from '../campaign_creation_rtb/components/adFormat/adFormat'

interface ReceivedVariationData {
	campaign: number
	article: number
	kertoo_article: number
	variation_no: number
	date_start: string | null
	active: boolean
	title: string
	summary: string
	image: string
	advertiser_title: string
	advertiser_domain: string
	theme: string
	call_to_action: string
	slug: string
	article_title: string
	imageId: string
	impression_pixel: string
	article_target_url: string
	variation_target_url: string
	created: Date
	updated: Date
	ad_format: AdFormats
	hiddenLocations: number[]
	impressions: number
	clicks: number
	ctr: number
	meta?: Record<string, unknown>
	client_id?: number
	videoId?: string
	videoPath?: string
}

export interface VariationData {
	id: number
	adGroup: number
	variation_no: number
	image: string
	advertiser_name: string
	title: string
	description: string
	cta: string
	theme: string
	targetUrl: string
	impressionPixel: string
	image_id: string
	is_active: boolean
	hidden_locations?: number[]
	impressions?: number
	clicks?: number
	ctr?: number
	videoPath?: string
	videoId?: string
}

interface Error {
	status?: number
}

const fetcher = async (input: RequestInfo, init: RequestInit = {}): Promise<VariationData[] | undefined> => {
	const res = await apiFetch(input, { credentials: 'same-origin', ...init })

	if (!res.ok) {
		const error = new Error('An error occurred while fetching the variation data.') as Error
		error.status = res.status
		captureExceptionWithSentry(error)
		throw error
	}
	const data = (await res.json()) as { status: string; data: ReceivedVariationData[]; message?: string }
	if (data.status === 'OK') {
		const variData = data.data
		return variData.map(
			(vari): VariationData => ({
				id: vari.article,
				adGroup: vari.kertoo_article,
				impressionPixel: vari.impression_pixel,
				is_active: !!vari.active,
				advertiser_name: vari.advertiser_title,
				description: vari.summary,
				cta: vari.call_to_action,
				targetUrl: vari.variation_target_url || vari.article_target_url,
				image_id: vari.imageId,
				variation_no: vari.variation_no,
				image: vari.image,
				title: vari.title,
				theme: vari.theme,
				hidden_locations: vari.hiddenLocations,
				impressions: vari.impressions,
				clicks: vari.clicks,
				ctr: vari.ctr,
				videoPath: vari.videoPath,
				videoId: vari.videoId,
			}),
		)
	} else if (data.status === 'error') {
		const error = new Error(data.message) as Error
		error.status = res.status
		captureExceptionWithSentry(error)
		throw error
	}
}

const settings = {
	revalidateIfStale: false,
	revalidateOnFocus: false,
	revalidateOnReconnect: false,
}

const useVariations = (campaign: number, withStats = false) => {
	const url = `/campaigns/${campaign}/variations?${withStats ? 'stats=1' : ''}`
	if (campaign === -1) {
		return {
			data: undefined,
			isLoading: false,
			error: '',
			mutate: undefined,
		}
	}
	const { data, error, mutate } = useSWR<VariationData[] | undefined, Error>(url, fetcher, {
		...settings,
		onErrorRetry: (fetchError, _key, _config, revalidate, { retryCount }) => {
			if (fetchError.status === 400) {
				return
			}

			if (campaign === -1 && retryCount && retryCount >= 10) {
				return
			}

			setTimeout(() => revalidate({ retryCount }), 5000)
		},
	})

	return {
		data,
		isLoading: !error && !data,
		error,
		mutate,
	}
}

export default useVariations
